import React, { useEffect, useState } from "react";

import { createColumnHelper } from "@tanstack/react-table";

import useUserContext from "../../contexts/UserContext";
import { Table } from "../../components/Shared/Table/Table";
import { PageHeader } from "../../components/Shared/PageHeader";
import { CheckCircleIcon, ExclamationCircleIcon } from "@heroicons/react/solid";
import { PencilAltIcon, TrashIcon, DuplicateIcon } from "@heroicons/react/outline";
import ActionButtonContainer from "../../components/Shared/ActionButtonContainer";
import ActionButton from "../../components/Shared/ActionButton";
import { useNavigate } from "react-router-dom";
import { ButtonInput } from "../../components/Shared/Inputs/ButtonInput";
import { INurtureCampaign, triggerOptions } from "../../models/nurtureCampaign/NurtureCampaign";
import { nurtureCampaignQueryKeys } from "../../data/nurtureCampaigns/queryKeys";
import ConfirmDelete from "../../components/Shared/ConfirmDelete";
import { useNurtureCampaignMutations } from "../../data/nurtureCampaigns/useNurtureCampaignMutations";
import formatDateTime from "../../utils/formatDateTime";

const colHelper = createColumnHelper<INurtureCampaign>();

export default function NurturingCampaigns() {
  const navigate = useNavigate();
  const { user: { agency } } = useUserContext();
  const { deleteCampaign, cloneCampaign } = useNurtureCampaignMutations(agency.id);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteCampaignId, setDeleteCampaignId] = useState<string>();

  useEffect(() => {
    document.title = "EstateTrack Portal | Nurturing Campaigns";
  }, []);

  const confirmDelete = () => {
    if (!deleteCampaignId) return;

    deleteCampaign.mutate(deleteCampaignId);

    setDeleteOpen(false);
    setDeleteCampaignId(undefined);
  }

  const cols = [
    colHelper.accessor("name", {
      header: "Campaign Name",
    }),
    colHelper.accessor("trigger", {
      header: "Trigger",
      cell: ({ row }) => {
        const trigger = triggerOptions.find(option => option.value === row.original.trigger.toString());
        return <>{trigger ? trigger.label : "Unknown"}</>;
      }
    }),
    colHelper.accessor("created", {
      header: "Created On",
      cell: (info) => (
        formatDateTime(info.getValue())
      )
    }),
    colHelper.accessor("enabled", {
      header: "Enabled",
      cell: (info) => (
        <span className={info.getValue() ? "text-green-500" : "text-red-500"}>
          {info.getValue() ? <CheckCircleIcon className="w-5 h-5" /> : <ExclamationCircleIcon className="w-5 h-5" />}
        </span>
      )
    }),
    colHelper.display({
      header: "Actions",
      cell: (info) => (
        <ActionButtonContainer>
          <ActionButton
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              cloneCampaign.mutate(info.row.original.id);
            }}
            tooltip="Clone"
            Icon={DuplicateIcon}
          />
          {info.row.original.agencyId !== "00000000-0000-0000-0000-000000000000" &&
            <>
              <ActionButton
                onClick={() => navigate(info.row.original.id)}
                tooltip="Edit"
                Icon={PencilAltIcon}
              />
              <ActionButton
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  setDeleteCampaignId(info.row.original.id);
                  setDeleteOpen(true);
                }}
                tooltip="Delete"
                Icon={TrashIcon}
              />
            </>
          }
        </ActionButtonContainer>
      )
    })
  ]


  return <>
    {/* @ts-ignore */}
    <ConfirmDelete
      itemName="Nurturing Campaign"
      open={deleteOpen}
      setOpen={setDeleteOpen}
      onConfirm={confirmDelete}
    />
    <div className="px-4 sm:px-6 lg:px-8">
      <PageHeader title="Nurturing Campaigns" description="View and manage nurturing campaigns here" />
      <div className="flex items-center justify-between">
        <ButtonInput
          label="Start New Nurturing Campaign"
          isSubmit={false}
          onClick={() => navigate("new")}
          classes=""
          children={undefined}
        />
      </div>
      <Table<INurtureCampaign>
        url={`/marketing/${agency.id}/nurture`}
        queryKey={nurtureCampaignQueryKeys.all(agency?.id)}
        columns={cols}
        onRowClick={(row) => navigate(`${row.original.id}`)}
      />
    </div>
  </>;
}